import { Alert, AlertIcon, Box, Flex, Text, useToast } from "@chakra-ui/react";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { urls } from "../../../../../urls";
import { LastChangedTag } from "../../../../../common/editing/LastChangedTag/LastChangedTag";
import { useEntityState } from "../../../../../common/editing/save-coordinator";
import {
  useGetExclusionGroundsTaxonomyQuery,
  useSubmitDoffinNoticeMutation,
  type OrgDto,
  type PersonDto,
  type SourcingEventV2Dto,
} from "../../../../../autogen/bff-api";
import { useApiError } from "../../../../../common/errors/useApiError";
import { StepNavigation } from "../../../../../common/StepNavigation";
import type { PublicEventStep } from "../wizardSteps";
import { useReadinessByField } from "../hooks/useReadinessByField";
import { AwardCriteriaSection } from "../components/summary/AwardCriteriaSection";
import { ClassificationSection } from "../components/summary/ClassificationSection";
import { ContractingAuthoritySection } from "../components/summary/ContractingAuthoritySection";
import { DeadlinesAndMilestonesSection } from "../components/summary/DeadlinesAndMilestonesSection";
import { DocumentsSection } from "../components/summary/DocumentsSection";
import { OverviewSection } from "../components/summary/OverviewSection";
import { ProcedureSection } from "../components/summary/ProcedureSection";
import { QualificationSection } from "../components/summary/QualificationSection";
import { computeExclusionGroundsSummary } from "../utils/computeExclusionGroundsSummary";
import { EspdPreviewSection } from "../components/espd/EspdPreviewSection";

interface Props {
  eventId: string;
  organization: OrgDto;
  currentEditor: PersonDto;
  responsibleBuyer: PersonDto;
  previousStep?: () => void;
  navigateToStep: (step: PublicEventStep) => void;
}

export const SummaryStep = ({
  eventId,
  organization,
  currentEditor,
  responsibleBuyer,
  previousStep,
  navigateToStep,
}: Props) => {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const event = useEntityState<SourcingEventV2Dto>(eventId);
  const { readinessFor, isPublishReady, isLoading: readinessLoading, isError: readinessError } =
    useReadinessByField(eventId);
  const { data: taxonomy } = useGetExclusionGroundsTaxonomyQuery();
  const [submitDoffin, { isLoading: isPublishing, isSuccess: hasPublished }] = useSubmitDoffinNoticeMutation();
  const apiError = useApiError();

  const hasPublishedPersisted = useMemo(
    () =>
      (event?.publicContext.publications ?? []).some(
        (p) => p.channel === "DOFFIN" && p.status !== "FAILED",
      ),
    [event?.publicContext.publications],
  );
  const isPublished = hasPublished || hasPublishedPersisted;

  const handlePublish = useCallback(async () => {
    try {
      await submitDoffin({ sourcingEventId: eventId }).unwrap();
      toast({
        title: t("Notice published to Doffin"),
        status: "success",
        duration: 6000,
      });
      navigate(urls.v2.sourcingEvents.view.go(eventId), { replace: true });
    } catch (e) {
      apiError.trigger(e);
    }
  }, [submitDoffin, eventId, toast, t, apiError, navigate]);

  const exclusionGroundsSummary = useMemo(
    () => computeExclusionGroundsSummary(taxonomy, event?.publicContext.activeExclusionGroundIds ?? []),
    [taxonomy, event?.publicContext.activeExclusionGroundIds],
  );

  if (!event) return null;

  return (
    <Box pb="8" pt="10">
      {readinessError && (
        <Alert status="error" borderRadius="lg" mb="6">
          <AlertIcon />
          <Text fontSize="sm">
            {t("Could not load publish readiness — refresh the page to retry. Section status indicators below may not be accurate.")}
          </Text>
        </Alert>
      )}
      <DeadlinesAndMilestonesSection
        event={event}
        readinessFor={readinessFor}
        onWizardStepNavigate={navigateToStep}
      />
      <OverviewSection event={event} readinessFor={readinessFor} onWizardStepNavigate={navigateToStep} />
      <ClassificationSection event={event} readinessFor={readinessFor} onWizardStepNavigate={navigateToStep} />
      <ProcedureSection event={event} readinessFor={readinessFor} onWizardStepNavigate={navigateToStep} />
      <QualificationSection
        event={event}
        readinessFor={readinessFor}
        onWizardStepNavigate={navigateToStep}
        exclusionGroundsSummary={exclusionGroundsSummary}
      />
      <AwardCriteriaSection event={event} readinessFor={readinessFor} onWizardStepNavigate={navigateToStep} />
      <DocumentsSection event={event} fieldStatuses={[]} onWizardStepNavigate={navigateToStep} />
      <ContractingAuthoritySection
        organization={organization}
        currentEditor={currentEditor}
        responsibleBuyer={responsibleBuyer}
        buyerLegalType={event.publicContext.buyerLegalType}
        readinessFor={readinessFor}
      />
      <EspdPreviewSection
        event={event}
        organization={organization}
        exclusionGroundsSummary={exclusionGroundsSummary}
      />

      <Flex justify="center" mb="6" mt="2">
        <LastChangedTag timestamp={event.modifiedAt} />
      </Flex>

      <StepNavigation
        onPrevious={previousStep}
        onNext={handlePublish}
        nextLabel={t("Publish to Doffin")}
        nextIsAction
        isNextLoading={isPublishing}
        nextLoadingText={t("Publishing…")}
        isNextDisabled={readinessLoading || readinessError || isPublishing || isPublished || !isPublishReady}
        nextTooltip={
          readinessError
            ? t("Could not load readiness status — try refreshing the page")
            : isPublishing
            ? t("Publishing…")
            : isPublished
            ? t("Already published to Doffin")
            : !isPublishReady && !readinessLoading
            ? t("Fill in all required fields above to enable publishing")
            : null
        }
      />
    </Box>
  );
};

